import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * FieldMap — an Egypt-bounded Leaflet map for the /try-your-field experience.
 * Plain Leaflet (imperative) rather than react-leaflet: leaflet is the
 * installed dependency and a single map needs no wrapper library.
 *
 * Click anywhere to drop a pin; the parent gets { lat, lng }. Polygon-draw is
 * a deliberate follow-up (PRD-01) — drop-a-pin is the mobile-primary mode and
 * is enough for a weather + ETc preview, which is area-independent.
 */

// Leaflet's default marker icons break under bundlers — point them at a CDN.
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

export default function FieldMap({ pin, onDrop, height = "420px" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onDropRef = useRef(onDrop);
  onDropRef.current = onDrop;

  // Initialise the map once.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [26.8, 30.8],
      zoom: 6,
      minZoom: 5,
      maxBounds: [
        [20.5, 23.5],
        [32.5, 37.5],
      ],
      maxBoundsViscosity: 0.7,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    map.on("click", (e) => {
      onDropRef.current?.({ lat: e.latlng.lat, lng: e.latlng.lng });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Keep the marker in sync with the pin prop.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pin) {
      if (markerRef.current) {
        markerRef.current.setLatLng([pin.lat, pin.lng]);
      } else {
        markerRef.current = L.marker([pin.lat, pin.lng]).addTo(map);
      }
      map.panTo([pin.lat, pin.lng]);
    } else if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, [pin]);

  return (
    <div
      ref={containerRef}
      style={{ height, width: "100%" }}
      className="rounded-xl overflow-hidden border border-gray-200"
    />
  );
}
